import { DefaultError } from '@tanstack/query-core';
import {
    UseQueryOptions,
    UseSuspenseQueryOptions,
    useQuery,
    useSuspenseQuery,
} from '@tanstack/react-query';
import { fetchTags } from 'api/tags';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Tag } from 'types/domain';
import { PaginatedData } from 'types/pagination';

interface UseSuspenseCurrentAccountTagsOptions
    extends Omit<
        UseSuspenseQueryOptions<PaginatedData<Tag> | null, DefaultError, Tag[], any>,
        'queryKey'
    > {
    enabled?: boolean;
}

const useSuspenseCurrentAccountTags = (options: UseSuspenseCurrentAccountTagsOptions = {}) => {
    const { accountId } = useCurrentAccount();
    const {
        enabled,
        staleTime = Infinity,
        select = data => data?.data ?? [],
        ...restOptions
    } = options;

    return useSuspenseQuery({
        queryKey: accountQueryKeys.tags(accountId),
        queryFn: () => conditionallyFetchOrNull(() => fetchTags(accountId), enabled),
        select,
        staleTime,
        ...restOptions,
    });
};

interface UseCurrentAccountTagOptions
    extends Omit<UseQueryOptions<PaginatedData<Tag>, DefaultError, Tag[], any>, 'queryKey'> {
    enabled?: boolean;
}

const useCurrentAccountTags = (options: UseCurrentAccountTagOptions = {}) => {
    const { accountId } = useCurrentAccount();
    const { select = data => data?.data ?? [], staleTime = Infinity, ...restOptions } = options;

    return useQuery({
        queryKey: accountQueryKeys.tags(accountId),
        queryFn: () => fetchTags(accountId),
        select,
        staleTime,
        ...restOptions,
    });
};

export { useSuspenseCurrentAccountTags, useCurrentAccountTags };
